import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TeksTimeBrandMark from '../components/TeksTimeBrandMark';
import { authAPI } from '../services/api';
import { generateDeviceId } from '../utils/deviceId';

/**
 * Web login only: password eligible only after legitimate interaction this page lifecycle.
 * Browser-restored / autofilled values are never accepted for /auth/login.
 */
export function shouldAcceptLoginPassword(
  password: string,
  passwordManuallyProvided: boolean,
): boolean {
  return Boolean(password.trim()) && passwordManuallyProvided;
}

/** Keys that do not count as entering a password (navigation / modifiers). */
function isNonEntryKey(key: string): boolean {
  return (
    key === 'Tab'
    || key === 'Shift'
    || key === 'Control'
    || key === 'Alt'
    || key === 'Meta'
    || key === 'CapsLock'
    || key === 'Escape'
    || key === 'ArrowLeft'
    || key === 'ArrowRight'
    || key === 'ArrowUp'
    || key === 'ArrowDown'
    || key === 'Home'
    || key === 'End'
    || key === 'PageUp'
    || key === 'PageDown'
    || key === 'Enter'
  );
}

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordManuallyProvided, setPasswordManuallyProvided] = useState(false);
  const [sharedWorkstation, setSharedWorkstation] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const passwordManuallyProvidedRef = useRef(false);
  /** True after the user focuses/clicks the password field this page lifecycle. */
  const passwordTouchedRef = useRef(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');

  const syncManualFlag = useCallback((value: boolean) => {
    passwordManuallyProvidedRef.current = value;
    setPasswordManuallyProvided(value);
  }, []);

  const clearPasswordField = useCallback(() => {
    setPassword('');
    syncManualFlag(false);
    const el = passwordInputRef.current;
    if (el) {
      el.value = '';
    }
  }, [syncManualFlag]);

  const resetLoginFields = useCallback(() => {
    setEmail('');
    passwordTouchedRef.current = false;
    clearPasswordField();
  }, [clearPasswordField]);

  // Every mount: application email/password start blank (never from client storage).
  useEffect(() => {
    resetLoginFields();
  }, [resetLoginFields]);

  // Resolve shared-workstation policy for this browser fingerprint (Device Restrictions).
  useEffect(() => {
    let cancelled = false;
    const loadPolicy = async () => {
      try {
        const deviceId = generateDeviceId();
        const response = await authAPI.getLoginDevicePolicy(deviceId);
        if (!cancelled && response.data?.success) {
          setSharedWorkstation(Boolean(response.data.data?.sharedWorkstation));
        }
      } catch {
        if (!cancelled) {
          setSharedWorkstation(false);
        }
      }
    };
    void loadPolicy();
    return () => {
      cancelled = true;
    };
  }, []);

  const markPasswordManuallyProvided = () => {
    syncManualFlag(true);
  };

  const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isNonEntryKey(e.key)) {
      return;
    }
    if ((e.ctrlKey || e.metaKey || e.altKey) && e.key.toLowerCase() !== 'v') {
      return;
    }
    markPasswordManuallyProvided();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Only clear a restored password before the user has touched the password field.
    // Clearing on every email keystroke forced the password input back to empty/read-only
    // and made Sign In fail with an empty password.
    if (!passwordTouchedRef.current) {
      clearPasswordField();
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Ignore silent autofill until the user has focused/clicked the password field
    // (or typed/pasted, which sets the manual flag via keydown/paste handlers).
    if (!passwordManuallyProvidedRef.current && !passwordTouchedRef.current) {
      e.target.value = '';
      setPassword('');
      return;
    }
    if (!passwordManuallyProvidedRef.current) {
      syncManualFlag(true);
    }
    setPassword(e.target.value);
  };

  /** Accept DOM value after real focus/click even if keydown events were swallowed. */
  const resolveSubmittedPassword = (): string => {
    const domPassword = passwordInputRef.current?.value ?? '';
    const fromState = password.trim();
    const candidate = fromState || domPassword.trim();
    if (!candidate) {
      return '';
    }
    if (passwordManuallyProvidedRef.current || passwordTouchedRef.current) {
      if (!passwordManuallyProvidedRef.current) {
        syncManualFlag(true);
      }
      return candidate;
    }
    return '';
  };

  const handlePasswordFocus = () => {
    passwordTouchedRef.current = true;
  };

  const rejectMissingPassword = () => {
    setError('Please enter your password.');
    clearPasswordField();
    window.setTimeout(() => {
      passwordInputRef.current?.focus();
    }, 0);
  };

  /** Single submit path for button click, Enter, and form submit. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const candidate = resolveSubmittedPassword();

    if (!shouldAcceptLoginPassword(candidate, passwordManuallyProvidedRef.current)) {
      rejectMissingPassword();
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(email, candidate);
      if (!result.success) {
        setError(result.message || 'Login failed. Please try again.');
        if (sharedWorkstation) {
          resetLoginFields();
        } else {
          clearPasswordField();
        }
        window.setTimeout(() => {
          passwordInputRef.current?.focus();
        }, 0);
      } else {
        resetLoginFields();
        if (returnUrl) {
          navigate(returnUrl);
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      if (sharedWorkstation) {
        resetLoginFields();
      } else {
        clearPasswordField();
      }
      window.setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (returnUrl) {
        navigate(returnUrl);
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, returnUrl, navigate]);

  const formAutoComplete = sharedWorkstation ? 'off' : 'on';
  const emailAutoComplete = sharedWorkstation ? 'off' : 'username';
  // Never use current-password — invites Chrome/Edge credential restore.
  const passwordAutoComplete = 'new-password';

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <TeksTimeBrandMark />
            <Typography component="h2" variant="h6" color="text.secondary" gutterBottom>
              Clock In/Out System
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 1, width: '100%' }}
              autoComplete={formAutoComplete}
              data-testid="login-form"
              data-shared-workstation={sharedWorkstation ? 'true' : 'false'}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name={sharedWorkstation ? 'teks-login-email' : 'email'}
                autoComplete={emailAutoComplete}
                autoFocus
                value={email}
                onChange={handleEmailChange}
                disabled={isLoading}
                inputProps={{
                  'data-testid': 'login-email',
                  ...(sharedWorkstation
                    ? {
                        'data-lpignore': 'true',
                        'data-1p-ignore': 'true',
                        'data-form-type': 'other',
                      }
                    : {}),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name={sharedWorkstation ? 'teks-login-password' : 'password'}
                label="Password"
                type="password"
                id="password"
                autoComplete={passwordAutoComplete}
                value={password}
                inputRef={passwordInputRef}
                onChange={handlePasswordChange}
                onKeyDown={handlePasswordKeyDown}
                onPaste={markPasswordManuallyProvided}
                onFocus={handlePasswordFocus}
                onClick={handlePasswordFocus}
                disabled={isLoading}
                inputProps={{
                  'data-testid': 'login-password',
                  'data-lpignore': 'true',
                  'data-1p-ignore': 'true',
                  'data-form-type': 'other',
                  autoCorrect: 'off',
                  autoCapitalize: 'off',
                  spellCheck: false,
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
                data-testid="login-submit"
              >
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot your password?
                </Link>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
